import { TournamentConditionTypeEnum, TournamentUserInfo } from 'config/types/tournament'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import { RowMiddle } from 'layout/Components/Row'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { colors } from 'theme/colors'
import Box from 'UIKit/Box/Box'
import TableCell from 'UIKit/Table/TableCell'
import { TableCellHistory } from 'UIKit/Table/TableHistory'
import TableRow from 'UIKit/Table/TableRow'
import Text from 'UIKit/Text'
import { getFullDisplayBalance } from 'utils/formatBalance'
import UserProfileModal from 'views/Profile/components/UserProfileModal'
import { DisplayMultiplier } from 'views/Profile/Transactions/components/styled'
import TournamentReward from 'views/Tournament/components/TournamentReward'
import DisplayRank from '../DisplayRank'

type CompactRowProps = {
  conditionType: TournamentConditionTypeEnum
  isActiveUser?: boolean
  isAboveActiveUser?: boolean
} & TournamentUserInfo

const CompactRow: React.FC<CompactRowProps> = ({
  displayName,
  userCode,
  prize,
  rank,
  multiplier,
  wagerAmount,
  conditionType,
  isActiveUser = false,
  isAboveActiveUser = false,
}) => {
  const [onPresentProfilePopup] = useModal(UserProfileModal)

  const handleOpenProfilePopup = () => {
    onPresentProfilePopup({ code: userCode })
  }
  const isUserAvailable = userCode?.length > 0
  const isPublicUser = displayName?.length > 0

  return (
    <StyledTableRow
      style={{
        backgroundColor: `${isActiveUser ? colors.primary : isAboveActiveUser ? colors.backgroundAlt8 : ''}`,
        borderRadius: isAboveActiveUser ? '4px' : '8px',
        overflow: 'hidden',
      }}
    >
      <StyledTableCell style={{ display: 'flex', justifyContent: 'center' }} paddingX="0 !important">
        <DisplayRank prize={prize} rank={rank} isActive={isActiveUser} />
      </StyledTableCell>

      <Column p="5px 12px">
        {isUserAvailable ? (
          <>
            <Box onClick={isPublicUser ? handleOpenProfilePopup : null}>
              {displayName ? (
                <DisplayText
                  style={{ cursor: isPublicUser && 'pointer' }}
                  color={isActiveUser || isAboveActiveUser ? 'text' : 'textTertiary'}
                  data-tooltip-content={displayName}
                  data-tooltip-id={displayName.length > 18 ? 'common-tip-top' : null}
                >
                  {displayName}
                </DisplayText>
              ) : (
                <RowMiddle>
                  <StyledUserHiddenAvatar width={16} height={16} mr="4px" $isActiveUser={isActiveUser}>
                    <Icons.HiddenAvatar width={16} height={16} />
                  </StyledUserHiddenAvatar>
                  <DisplayText>
                    <Trans>Hidden</Trans>
                  </DisplayText>
                </RowMiddle>
              )}
            </Box>
            <Box mt="4px">
              <DisplayText color={isActiveUser ? 'text' : '#7A7A7A'} fontSize="10px !important">
                {conditionType === TournamentConditionTypeEnum.Wager ? (
                  `$${getFullDisplayBalance(wagerAmount)}`
                ) : (
                  <DisplayMultiplier
                    amount={multiplier}
                    color={isActiveUser ? 'text' : '#7A7A7A'}
                    fontSize="10px !important"
                  />
                )}
              </DisplayText>
            </Box>
          </>
        ) : (
          <Text color="textAlt5" fontSize="12px" fontWeight={600} p="6px 4px">
            -
          </Text>
        )}
      </Column>

      <StyledTableCell
        maxWidth={['130px', '', '', '150px']}
        position="relative"
        style={{
          justifyContent: 'flex-end',
        }}
      >
        {prize ? (
          <StyledTournamentReward
            rewardDetails={prize}
            fontSize="12px"
            textAlign="left"
            $isActive
            isDisplayTooltip
            tooltipId={`tournament-reward-${userCode}`}
          />
        ) : (
          <Text>_</Text>
        )}
      </StyledTableCell>
    </StyledTableRow>
  )
}

export default CompactRow

const StyledUserHiddenAvatar = styled(Box)<{ $isActiveUser }>`
  svg {
    path {
      fill: ${({ $isActiveUser }) => ($isActiveUser ? '#fff' : '')};
    }
  }
`

const StyledTableRow = styled(TableRow)`
  width: 100%;
  border-top: ${({ theme: { colors } }) => `0.25px solid ${colors.stroke}`};

  &:last-child {
    ${TableCell} {
      border-bottom: ${({ theme: { colors } }) => `0.25px solid ${colors.stroke}`};
    }

    td:first-child ${TableCell} {
      border-bottom-left-radius: 8px;
    }

    td:last-child ${TableCell} {
      border-bottom-right-radius: 8px;
    }
  }
`

const StyledTournamentReward = styled(TournamentReward)<{ $isActive }>`
  & > * {
    font-size: 12px;
    color: ${({ theme: { colors }, $isActive }) => $isActive && colors.text};
  }
`

const StyledTableCell = styled(TableCellHistory)`
  padding: 6px 4px;
`

const DisplayText = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.24px;
`
